import { useState, useCallback, useRef, useEffect } from 'react';
import * as adService from '../services/adService';
import type {
  Ad,
  AdListItem,
  CreateAdRequest,
  UpdateAdRequest,
  ListAdsParams,
  UseAdsReturn,
  ContactSellerResponse,
} from '../types/ad';

// Cache for storing fetched ads
interface AdsCache {
  key: string;
  data: AdListItem[];
  total: number;
  timestamp: number;
}

const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes
let globalAdsCache: AdsCache | null = null;
// Prefetch buffer for the next page to improve perceived latency on "Load more".
const prefetchRef: {
  current: {
    key: string;
    page: number;
    ads: AdListItem[];
    total: number;
  } | null;
} = { current: null };

/** Flatten a full `Ad` (with nested category) into a list-item shape. */
function toListItem(ad: Ad): AdListItem {
  const categoryId =
    typeof ad.category === 'string' ? ad.category : ad.category?.id ?? '';
  return {
    ...ad,
    category: categoryId,
  } as AdListItem;
}

export function useAds(): UseAdsReturn {
  // State
  const [ads, setAds] = useState<AdListItem[]>([]);
  const [myAds, setMyAds] = useState<AdListItem[]>([]);
  const [currentAd, setCurrentAd] = useState<Ad | null>(null);
  const [totalAds, setTotalAds] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Refs
  const isMounted = useRef(true);
  const currentParams = useRef<ListAdsParams>({});
  const abortControllerRef = useRef<AbortController | null>(null);

  // Track mounted lifecycle. Must flip to `true` on every mount so that
  // React 18 StrictMode's double-invocation (which unmounts once before
  // the real mount) doesn't leave `isMounted.current` stuck at `false`.
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Generate cache key from params
  const getCacheKey = useCallback((params: ListAdsParams): string => {
    return JSON.stringify(params);
  }, []);

  // Check if cache is valid
  const isCacheValid = useCallback((key: string): boolean => {
    if (!globalAdsCache) return false;
    if (globalAdsCache.key !== key) return false;
    return Date.now() - globalAdsCache.timestamp < CACHE_DURATION;
  }, []);

  /**
   * Fetch ads with filters
   */
  const fetchAds = useCallback(async (params: ListAdsParams = {}) => {
    if (!isMounted.current) return;

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const cacheKey = getCacheKey(params);
    
    // Check cache first
    if (isCacheValid(cacheKey)) {
      // Serve cached data immediately for instant UI
      setAds(globalAdsCache!.data);
      setTotalAds(globalAdsCache!.total);
      // Also kick off a background refresh to keep data fresh (SWR-style)
      (async () => {
        try {
          const fresh = await adService.listAds({ ...params, page: params.page ?? 1 });
          if (!isMounted.current) return;
          // Avoid clobbering if params changed during refresh
          if (getCacheKey(params) !== cacheKey) return;
          setAds(fresh.ads);
          setTotalAds(fresh.total);
          setHasMore(fresh.ads.length < fresh.total);
          setCurrentPage(params.page ?? 1);
          globalAdsCache = { key: cacheKey, data: fresh.ads, total: fresh.total, timestamp: Date.now() };
          // Prime prefetch for next page
          const nextPage = (params.page ?? 1) + 1;
          if (fresh.ads.length < fresh.total) {
            const next = await adService.listAds({ ...params, page: nextPage });
            prefetchRef.current = { key: cacheKey, page: nextPage, ads: next.ads, total: next.total };
          } else {
            prefetchRef.current = null;
          }
        } catch {
          // Ignore background refresh errors; cached data is already shown
        }
      })();
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      currentParams.current = params;

      const response = await adService.listAds(params);

      if (!isMounted.current) return;

      const page = params.page ?? 1;
      setAds(response.ads);
      setTotalAds(response.total);
      setHasMore(response.ads.length < response.total);
      setCurrentPage(page);

      // Update cache
      globalAdsCache = {
        key: cacheKey,
        data: response.ads,
        total: response.total,
        timestamp: Date.now(),
      };

      // Prime prefetch for page 2 (or next page)
      const nextPage = (params.page ?? 1) + 1;
      if (response.ads.length < response.total) {
        try {
          const next = await adService.listAds({ ...params, page: nextPage });
          prefetchRef.current = { key: cacheKey, page: nextPage, ads: next.ads, total: next.total };
        } catch {
          prefetchRef.current = null;
        }
      } else {
        prefetchRef.current = null;
      }
    } catch (err) {
      if (!isMounted.current) return;
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch ads';
      setError(errorMessage);
      console.error('Error fetching ads:', err);
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [getCacheKey, isCacheValid]);

  /**
   * Load more ads (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!isMounted.current || isLoadingMore || !hasMore) return;

    try {
      setIsLoadingMore(true);

      const nextPage = currentPage + 1;
      const params = { ...currentParams.current, page: nextPage };

      const key = getCacheKey({ ...currentParams.current, page: currentPage });
      const pref = prefetchRef.current;
      if (pref && pref.key === key && pref.page === nextPage) {
        // Use prefetched data for instant load
        if (!isMounted.current) return;
        setAds(prev => {
          const merged = [...prev, ...pref.ads];
          setHasMore(merged.length < pref.total);
          return merged;
        });
        setCurrentPage(nextPage);
        // Prefetch the following page
        const afterNext = nextPage + 1;
        try {
          const next = await adService.listAds({ ...currentParams.current, page: afterNext });
          prefetchRef.current = { key, page: afterNext, ads: next.ads, total: next.total };
        } catch {
          prefetchRef.current = null;
        }
      } else {
        // Fallback: fetch normally
        const response = await adService.listAds(params);
        if (!isMounted.current) return;
        setAds(prev => {
          const merged = [...prev, ...response.ads];
          setHasMore(merged.length < response.total);
          return merged;
        });
        setCurrentPage(nextPage);
        // Prefetch the next page
        const afterNext = nextPage + 1;
        try {
          const next = await adService.listAds({ ...currentParams.current, page: afterNext });
          prefetchRef.current = { key, page: afterNext, ads: next.ads, total: next.total };
        } catch {
          prefetchRef.current = null;
        }
      }
    } catch (err) {
      if (!isMounted.current) return;
      console.error('Error loading more ads:', err);
    } finally {
      if (isMounted.current) {
        setIsLoadingMore(false);
      }
    }
  }, [currentPage, hasMore, isLoadingMore]);

  /**
   * Refresh ads (clear cache and refetch)
   */
  const refreshAds = useCallback(async () => {
    globalAdsCache = null;
    prefetchRef.current = null;
    await fetchAds(currentParams.current);
  }, [fetchAds]);

  /**
   * Fetch user's own ads
   */
  const fetchMyAds = useCallback(async () => {
    if (!isMounted.current) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await adService.getMyAds();

      if (!isMounted.current) return;

      setMyAds(response.ads);
    } catch (err) {
      if (!isMounted.current) return;
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch your ads';
      setError(errorMessage);
      console.error('Error fetching my ads:', err);
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  /**
   * Fetch single ad details
   */
  const fetchAdDetails = useCallback(async (id: string): Promise<Ad | null> => {
    if (!isMounted.current) return null;

    try {
      setIsLoading(true);
      setError(null);

      const response = await adService.getAdDetails(id);

      if (!isMounted.current) return null;

      setCurrentAd(response.ad);
      return response.ad;
    } catch (err) {
      if (!isMounted.current) return null;
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch ad details';
      setError(errorMessage);
      console.error('Error fetching ad details:', err);
      return null;
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  /**
   * Create a new ad
   */
  const createAd = useCallback(async (
    data: CreateAdRequest,
    images?: File[]
  ): Promise<Ad | null> => {
    if (!isMounted.current) return null;

    try {
      setIsLoading(true);
      setError(null);

      const response = await adService.createAd(data, images);

      if (!isMounted.current) return null;

      // Update local state
      setMyAds(prev => [toListItem(response.ad), ...prev]);

      // Invalidate cache
      globalAdsCache = null;

      return response.ad;
    } catch (err) {
      if (!isMounted.current) return null;
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to create ad';
      setError(errorMessage);
      console.error('Error creating ad:', err);
      return null;
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  /**
   * Update an existing ad
   */
  const updateAd = useCallback(async (
    id: string,
    data: UpdateAdRequest
  ): Promise<Ad | null> => {
    if (!isMounted.current) return null;

    try {
      setIsLoading(true);
      setError(null);

      const updatedAd = await adService.updateAd(id, data);

      if (!isMounted.current) return null;

      // Update local state
      const listShape = toListItem(updatedAd);
      setMyAds(prev =>
        prev.map(ad => (ad.id === id ? listShape : ad))
      );

      if (currentAd?.id === id) {
        setCurrentAd(updatedAd);
      }

      // Invalidate cache
      globalAdsCache = null;

      return updatedAd;
    } catch (err) {
      if (!isMounted.current) return null;
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to update ad';
      setError(errorMessage);
      console.error('Error updating ad:', err);
      return null;
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [currentAd]);

  /**
   * Delete an ad
   */
  const deleteAd = useCallback(async (id: string): Promise<boolean> => {
    if (!isMounted.current) return false;

    try {
      setIsLoading(true);
      
      await adService.deleteAd(id);

      if (!isMounted.current) return false;

      // Update local state
      setMyAds(prev => prev.filter(ad => ad.id !== id));
      setAds(prev => prev.filter(ad => ad.id !== id));
      
      if (currentAd?.id === id) {
        setCurrentAd(null);
      }

      // Invalidate cache
      globalAdsCache = null;

      return true;
    } catch (err) {
      if (!isMounted.current) return false;
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete ad';
      setError(errorMessage);
      console.error('Error deleting ad:', err);
      return false;
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [currentAd]);

  /**
   * Mark ad as sold
   */
  const markAsSold = useCallback(async (id: string): Promise<boolean> => {
    if (!isMounted.current) return false;

    try {
      setIsLoading(true);
      
      const updatedAd = await adService.markAdAsSold(id);

      if (!isMounted.current) return false;

      // Update local state
      const listShape = toListItem(updatedAd);
      setMyAds(prev =>
        prev.map(ad => (ad.id === id ? listShape : ad))
      );

      if (currentAd?.id === id) {
        setCurrentAd(updatedAd);
      }

      // Invalidate cache
      globalAdsCache = null;

      return true;
    } catch (err) {
      if (!isMounted.current) return false;
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to mark ad as sold';
      setError(errorMessage);
      console.error('Error marking ad as sold:', err);
      return false;
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [currentAd]);

  /**
   * Toggle ad active/inactive
   */
  const toggleActive = useCallback(async (id: string): Promise<boolean> => {
    if (!isMounted.current) return false;

    try {
      setIsLoading(true);

      const updatedAd = await adService.toggleAdActive(id);

      if (!isMounted.current) return false;

      const listShape = toListItem(updatedAd);
      setMyAds(prev => prev.map(a => (a.id === id ? listShape : a)));
      if (currentAd?.id === id) setCurrentAd(updatedAd);

      return true;
    } catch (err) {
      if (!isMounted.current) return false;
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle ad';
      setError(errorMessage);
      console.error('Error toggling ad:', err);
      return false;
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [currentAd]);

  /**
   * Get seller contact information
   */
  const contactSeller = useCallback(async (
    id: string
  ): Promise<ContactSellerResponse['data'] | null> => {
    if (!isMounted.current) return null;

    try {
      const contactInfo = await adService.contactSeller(id);
      return contactInfo;
    } catch (err) {
      if (!isMounted.current) return null;
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to get contact info';
      setError(errorMessage);
      console.error('Error contacting seller:', err);
      return null;
    }
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setAds([]);
    setMyAds([]);
    setCurrentAd(null);
    setTotalAds(0);
    setIsLoading(false);
    setIsLoadingMore(false);
    setError(null);
    setHasMore(true);
    setCurrentPage(1);
    currentParams.current = {};
    prefetchRef.current = null;
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    ads,
    myAds,
    currentAd,
    totalAds,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    currentPage,

    // Actions
    fetchAds,
    fetchMyAds,
    fetchAdDetails,
    createAd,
    updateAd,
    deleteAd,
    markAsSold,
    toggleActive,
    contactSeller,
    loadMore,
    refreshAds,

    // Reset
    reset,
    clearError,
  };
}

/**
 * Hook for managing a single ad creation
 * Useful for the PostAdPage
 */
export function useCreateAd() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdAd, setCreatedAd] = useState<Ad | null>(null);

  const createAd = useCallback(async (
    data: CreateAdRequest,
    images?: File[]
  ): Promise<Ad | null> => {
    try {
      setIsSubmitting(true);
      setError(null);
      setIsSuccess(false);

      const response = await adService.createAd(data, images);
      
      setCreatedAd(response.ad);
      setIsSuccess(true);
      
      // Invalidate global cache
      globalAdsCache = null;
      
      return response.ad;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create ad';
      setError(errorMessage);
      console.error('Error creating ad:', err);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setIsSubmitting(false);
    setIsSuccess(false);
    setError(null);
    setCreatedAd(null);
  }, []);

  return {
    isSubmitting,
    isSuccess,
    error,
    createdAd,
    createAd,
    reset,
  };
}

// ─────────────────────────────────────────────────────────────
// useAdDetails — single-ad fetch hook for detail pages
// ─────────────────────────────────────────────────────────────

export interface UseAdDetailsReturn {
  ad: Ad | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches a single ad by UUID. Automatically re-runs when `id` changes
 * and aborts any in-flight request on unmount / id change.
 */
export function useAdDetails(id: string | undefined): UseAdDetailsReturn {
  const [ad, setAd] = useState<Ad | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!id) {
      setAd(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const response = await adService.getAdDetails(id);
      if (!mounted.current || requestId !== requestIdRef.current) return;
      setAd(response.ad);
    } catch (err) {
      if (!mounted.current || requestId !== requestIdRef.current) return;
      const msg = err instanceof Error ? err.message : 'Failed to load ad';
      setError(msg);
      setAd(null);
    } finally {
      if (mounted.current && requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  return { ad, isLoading, error, refetch: load };
}

